'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Search, Menu, X, User, LogOut, ChevronDown, ImageIcon, LayoutDashboard, Users, FileText, Settings } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useArticles } from '@/context/ArticlesContext';

interface SearchResult {
  id: string;
  title: string;
  section: string;
  excerpt: string;
  href: string;
  type: 'article' | 'material';
}

function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-primary/20 text-gold rounded px-0.5">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

const DEFAULT_LOGO = '/assets/images/images-1788673570475.jpeg';

export default function Header() {
  const { currentUser, isAdmin, isAuthenticated, logout } = useAuth();
  const { searchArticles } = useArticles();
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [logoSrc, setLogoSrc] = useState<string>(DEFAULT_LOGO);
  const searchRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchQuery.length > 1) {
      const articles = searchArticles(searchQuery);
      const results: SearchResult[] = articles.slice(0, 6).map(a => ({
        id: a.id,
        title: a.title,
        section: a.category,
        excerpt: a.content.replace(/#+\s/g, '').slice(0, 80),
        href: `/article-material-detail-page?id=${a.id}`,
        type: 'article',
      }));
      setSearchResults(results);
      setShowResults(results.length > 0);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchQuery, searchArticles]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowResults(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setLogoSrc(url);
    setUserMenuOpen(false);
  }

  function handleLogout() {
    logout();
    router.push('/login');
  }

  const userName = currentUser?.name || 'User';
  const displayName = userName.charAt(0).toUpperCase() + userName.slice(1);

  return (
    <header className="header-luxury sticky top-0 z-50">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
        <div className="flex items-center gap-4 h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 flex-shrink-0">
            <div className="relative w-8 h-8 rounded overflow-hidden flex-shrink-0">
              <Image src={logoSrc} alt="SilkCards Logo" fill className="object-contain" priority unoptimized />
            </div>
            <div className="hidden sm:block">
              <span className="font-serif text-xl font-600 text-gold tracking-wide leading-none block">SilkCards</span>
              <span className="text-[10px] text-muted-foreground tracking-widest uppercase leading-none">Knowledge Base</span>
            </div>
          </Link>

          <div className="hidden lg:block w-px h-8 bg-border mx-2" />

          {/* Nav links */}
          <nav className="hidden lg:flex items-center gap-1">
            <Link href="/" className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-surface-2 transition-all duration-150">
              Knowledge Base
            </Link>
            <Link href="/section-article-list-page" className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-surface-2 transition-all duration-150">
              Articles
            </Link>
            {isAdmin && (
              <>
                <Link href="/admin" className="px-3 py-2 text-sm text-muted-foreground hover:text-gold rounded-lg hover:bg-surface-2 transition-all duration-150 flex items-center gap-1.5">
                  <LayoutDashboard className="w-3.5 h-3.5" />
                  Dashboard
                </Link>
                <Link href="/admin/articles" className="px-3 py-2 text-sm text-muted-foreground hover:text-gold rounded-lg hover:bg-surface-2 transition-all duration-150 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" />
                  Manage Articles
                </Link>
                <Link href="/admin/users" className="px-3 py-2 text-sm text-muted-foreground hover:text-gold rounded-lg hover:bg-surface-2 transition-all duration-150 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  Users
                </Link>
              </>
            )}
          </nav>

          {/* Search */}
          <div ref={searchRef} className="flex-1 max-w-xl relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Search articles, materials, SOPs..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.length > 1 && setShowResults(true)}
                className="search-luxury w-full pl-9 pr-4 py-2 rounded-lg text-sm"
              />
              {searchQuery && (
                <button onClick={() => { setSearchQuery(''); setShowResults(false); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {showResults && (
              <div className="absolute top-full left-0 right-0 mt-2 card-luxury-static shadow-gold-lg z-50 overflow-hidden animate-fade-in">
                <div className="p-2">
                  <p className="text-[11px] text-muted-foreground px-3 py-1.5 font-medium uppercase tracking-wider">
                    {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for &ldquo;{searchQuery}&rdquo;
                  </p>
                  {searchResults.map(result => (
                    <Link key={`sr-${result.id}`} href={result.href} onClick={() => { setShowResults(false); setSearchQuery(''); }}
                      className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-surface-2 transition-colors group">
                      <span className="text-base mt-0.5 flex-shrink-0">📄</span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground group-hover:text-gold transition-colors truncate">
                          {highlightMatch(result.title, searchQuery)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {highlightMatch(result.excerpt, searchQuery)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* User menu */}
          {isAuthenticated ? (
            <div ref={userMenuRef} className="relative flex-shrink-0">
              <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-surface-2 transition-all duration-150 group">
                <div className="w-7 h-7 rounded-full bg-gold-gradient flex items-center justify-center text-xs font-700 text-primary-foreground overflow-hidden">
                  {currentUser?.profilePhoto ? (
                    <Image src={currentUser.profilePhoto} alt={userName} width={28} height={28} className="w-full h-full object-cover" unoptimized />
                  ) : (
                    userName.charAt(0).toUpperCase()
                  )}
                </div>
                <span className="hidden sm:block text-sm font-medium text-foreground group-hover:text-gold transition-colors">
                  Hi, {isAdmin ? 'Admin' : displayName}!
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 card-luxury-static shadow-gold z-50 overflow-hidden animate-fade-in">
                  <div className="p-1">
                    <div className="px-3 py-2 border-b border-border mb-1">
                      <p className="text-sm font-medium text-foreground">{isAdmin ? 'Admin' : displayName}</p>
                      <p className="text-xs text-muted-foreground truncate">{currentUser?.email}</p>
                      {isAdmin && <span className="tag-gold mt-1 inline-block">Admin</span>}
                    </div>

                    {isAdmin && (
                      <>
                        <Link href="/admin" onClick={() => setUserMenuOpen(false)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-gold hover:bg-surface-2 rounded-lg transition-colors">
                          <LayoutDashboard className="w-4 h-4" />
                          Admin Dashboard
                        </Link>
                        <Link href="/admin/articles" onClick={() => setUserMenuOpen(false)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-gold hover:bg-surface-2 rounded-lg transition-colors">
                          <FileText className="w-4 h-4" />
                          Manage Articles
                        </Link>
                        <Link href="/admin/users" onClick={() => setUserMenuOpen(false)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-gold hover:bg-surface-2 rounded-lg transition-colors">
                          <Users className="w-4 h-4" />
                          Manage Users
                        </Link>
                        <Link href="/admin/categories" onClick={() => setUserMenuOpen(false)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-gold hover:bg-surface-2 rounded-lg transition-colors">
                          <Settings className="w-4 h-4" />
                          Categories
                        </Link>
                        <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                        <button onClick={() => logoInputRef.current?.click()}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-gold hover:bg-surface-2 rounded-lg transition-colors">
                          <ImageIcon className="w-4 h-4" />
                          Change Logo
                        </button>
                        <div className="border-t border-border my-1" />
                      </>
                    )}

                    {!isAdmin && (
                      <Link href="/profile" onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-surface-2 rounded-lg transition-colors">
                        <User className="w-4 h-4" />
                        My Profile
                      </Link>
                    )}

                    <button onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-surface-2 rounded-lg transition-colors">
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link href="/login" className="btn-gold px-4 py-2 rounded-lg text-sm font-semibold flex-shrink-0">
              Sign In
            </Link>
          )}

          {/* Mobile menu toggle */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 rounded-lg hover:bg-surface-2 transition-colors flex-shrink-0">
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border py-3 space-y-1 animate-fade-in">
            <Link href="/" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-surface-2">Knowledge Base</Link>
            <Link href="/section-article-list-page" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-surface-2">Articles</Link>
            {isAdmin && (
              <>
                <Link href="/admin" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 text-sm text-gold hover:bg-surface-2 rounded-lg">Dashboard</Link>
                <Link href="/admin/articles" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 text-sm text-gold hover:bg-surface-2 rounded-lg">Manage Articles</Link>
                <Link href="/admin/users" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 text-sm text-gold hover:bg-surface-2 rounded-lg">Manage Users</Link>
              </>
            )}
            {isAuthenticated && (
              <button onClick={handleLogout} className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-surface-2 rounded-lg">Sign Out</button>
            )}
          </div>
        )}
      </div>
    </header>
  );
}